import { documentUrl as fileHref } from "@/lib/images";
import { DatePickerTR } from "@/components/DatePickerTR";
import { useEffect, useMemo, useState } from "react";
import api from "@/lib/api";
import { Input } from "@/components/ui/input";
import { fmtTL, ExpiryBadge } from "@/components/StatusBadge";
import {
  CalendarDays, ChevronDown, TrendingUp, TrendingDown, Truck,
  Package, Receipt, Loader2, Building2,
} from "lucide-react";

const PRESETS = [
  { id: "today", label: "Bugün" },
  { id: "yesterday", label: "Dün" },
  { id: "week", label: "Bu Hafta" },
  { id: "month", label: "Bu Ay" },
  { id: "lastMonth", label: "Geçen Ay" },
  { id: "custom", label: "Özel" },
];

const startOfDay = (d) => { const x = new Date(d); x.setHours(0, 0, 0, 0); return x; };
const endOfDay = (d) => { const x = new Date(d); x.setHours(23, 59, 59, 999); return x; };


function rangeFor(preset) {
  const now = new Date();
  if (preset === "today") return { start: startOfDay(now), end: endOfDay(now) };
  if (preset === "yesterday") { const y = new Date(now); y.setDate(now.getDate() - 1); return { start: startOfDay(y), end: endOfDay(y) }; }
  if (preset === "week") { const d = new Date(now); const dow = (d.getDay() + 6) % 7; d.setDate(d.getDate() - dow); return { start: startOfDay(d), end: endOfDay(now) }; }
  if (preset === "month") return { start: startOfDay(new Date(now.getFullYear(), now.getMonth(), 1)), end: endOfDay(now) };
  if (preset === "lastMonth") return { start: startOfDay(new Date(now.getFullYear(), now.getMonth() - 1, 1)), end: endOfDay(new Date(now.getFullYear(), now.getMonth(), 0)) };
  return null;
}

export default function ReceiptHistory({ suppliers = [] }) {
  const [preset, setPreset] = useState("month");
  const [cStart, setCStart] = useState("");
  const [cEnd, setCEnd] = useState("");
  const [supplier, setSupplier] = useState("");
  const [receipts, setReceipts] = useState([]);
  const [summary, setSummary] = useState(null);
  const [loading, setLoading] = useState(false);
  const [expanded, setExpanded] = useState(null);

  const { start, end } = useMemo(() => {
    if (preset === "custom") {
      if (!cStart || !cEnd) return { start: null, end: null };
      return { start: startOfDay(cStart), end: endOfDay(cEnd) };
    }
    return rangeFor(preset);
  }, [preset, cStart, cEnd]);

  useEffect(() => {
    if (!start || !end) return;
    setLoading(true);
    const s = start.toISOString(), e = end.toISOString();
    const p = new URLSearchParams({ start: s, end: e });
    if (supplier) p.set("supplier", supplier);
    Promise.all([
      api.get(`/goods-receipts?${p}`),
      api.get(`/goods-receipts/summary?start=${encodeURIComponent(s)}&end=${encodeURIComponent(e)}`),
    ])
      .then(([r1, r2]) => { setReceipts(r1.data); setSummary(r2.data); })
      .catch(() => {})
      .finally(() => setLoading(false));
  }, [start, end, supplier]);

  const change = summary?.change_pct;

  return (
    <div className="space-y-4 fade-in">
      {/* Filters */}
      <div className="rounded-md bg-white p-3 ring-1 ring-inset ring-slate-200">
        <div className="flex flex-wrap items-center gap-2">
          <CalendarDays className="h-4 w-4 text-slate-400" />
          {PRESETS.map((p) => (
            <button key={p.id} data-testid={`hist-preset-${p.id}`} onClick={() => setPreset(p.id)}
              className={`rounded-full px-3 py-1.5 text-xs font-semibold transition-colors ${preset === p.id ? "bg-brand-green text-white" : "bg-slate-100 text-slate-600 hover:bg-slate-200"}`}>
              {p.label}
            </button>
          ))}
          <div className="relative ml-auto">
            <select data-testid="hist-supplier" value={supplier} onChange={(e) => setSupplier(e.target.value)}
              className="h-9 appearance-none rounded-md border border-input bg-white pl-3 pr-8 text-xs font-medium text-slate-700 focus:outline-none focus:ring-2 focus:ring-brand-green">
              <option value="">Tüm Tedarikçiler</option>
              {suppliers.map((s) => <option key={s.id} value={s.name}>{s.name}</option>)}
            </select>
            <ChevronDown className="pointer-events-none absolute right-2 top-1/2 h-3.5 w-3.5 -translate-y-1/2 text-slate-400" />
          </div>
        </div>
        {preset === "custom" && (
          <div className="mt-3 flex flex-wrap items-center gap-2">
            <div><label className="text-[10px] text-slate-400">Başlangıç</label>
              <DatePickerTR testid="hist-custom-start" value={cStart} onChange={(v) => setCStart(v)} className="mt-0 h-9 w-40" /></div>
            <div><label className="text-[10px] text-slate-400">Bitiş</label>
              <DatePickerTR testid="hist-custom-end" value={cEnd} onChange={(v) => setCEnd(v)} className="mt-0 h-9 w-40" /></div>
          </div>
        )}
      </div>

      {/* Summary cards */}
      <div className="grid grid-cols-2 gap-3 lg:grid-cols-4">
        <div className="rounded-md bg-brand-green p-4 text-white">
          <p className="text-xs text-emerald-100">Toplam Harcama</p>
          <p data-testid="hist-total" className="tabular font-heading text-2xl font-bold">{fmtTL(summary?.total ?? 0)}</p>
          {change != null && (
            <p className={`mt-1 inline-flex items-center gap-1 text-[11px] font-semibold ${change >= 0 ? "text-amber-200" : "text-emerald-100"}`}>
              {change >= 0 ? <TrendingUp className="h-3 w-3" /> : <TrendingDown className="h-3 w-3" />}
              önceki döneme göre %{Math.abs(change)}
            </p>
          )}
          {change == null && <p className="mt-1 text-[11px] text-emerald-200">önceki dönem verisi yok</p>}
        </div>
        <div className="rounded-md bg-white p-4 ring-1 ring-inset ring-slate-200">
          <p className="overline flex items-center gap-1"><Receipt className="h-3 w-3" /> Mal Kabul</p>
          <p className="tabular font-heading text-2xl font-bold text-slate-900">{summary?.count ?? 0}</p>
          <p className="text-[11px] text-slate-400">fiş</p>
        </div>
        <div className="rounded-md bg-white p-4 ring-1 ring-inset ring-slate-200">
          <p className="overline flex items-center gap-1"><Package className="h-3 w-3" /> Toplam Kalem</p>
          <p className="tabular font-heading text-2xl font-bold text-slate-900">{summary?.items ?? 0}</p>
          <p className="text-[11px] text-slate-400">satır</p>
        </div>
        <div className="rounded-md bg-white p-4 ring-1 ring-inset ring-slate-200">
          <p className="overline flex items-center gap-1"><Building2 className="h-3 w-3" /> Tedarikçi Kırılımı</p>
          <div className="mt-1 max-h-16 space-y-0.5 overflow-y-auto no-scrollbar">
            {(summary?.by_supplier || []).length === 0 && <p className="text-[11px] text-slate-400">—</p>}
            {(summary?.by_supplier || []).map((b, i) => (
              <div key={i} className="flex items-center justify-between text-[11px]">
                <span className="truncate text-slate-600">{b.supplier}</span>
                <span className="tabular font-semibold text-slate-800">{fmtTL(b.total)}</span>
              </div>
            ))}
          </div>
        </div>
      </div>

      {/* Receipt list */}
      <div className="rounded-md bg-white ring-1 ring-inset ring-slate-200">
        <div className="flex items-center justify-between border-b border-slate-100 px-4 py-3">
          <p className="font-heading font-bold text-slate-800">Mal Kabul Kayıtları</p>
          {loading && <Loader2 className="h-4 w-4 animate-spin text-slate-400" />}
        </div>
        {receipts.length === 0 && !loading ? (
          <p className="p-10 text-center text-sm text-slate-400">Seçili dönemde mal kabul kaydı yok.</p>
        ) : (
          <div className="divide-y divide-slate-100">
            {receipts.map((r) => {
              const open = expanded === r.id;
              return (
                <div key={r.id} data-testid={`hist-receipt-${r.id}`}>
                  <button onClick={() => setExpanded(open ? null : r.id)}
                    data-testid={`hist-receipt-toggle-${r.id}`}
                    className="flex w-full items-center gap-3 px-4 py-3 text-left hover:bg-slate-50">
                    <div className="flex h-9 w-9 shrink-0 items-center justify-center rounded-md bg-emerald-50 text-emerald-700">
                      <Truck className="h-4 w-4" />
                    </div>
                    <div className="min-w-0 flex-1">
                      <p className="truncate text-sm font-semibold text-slate-800">{r.supplier}</p>
                      <p className="font-mono text-[11px] text-slate-400">
                        {r.receipt_no} · {new Date(r.created_at).toLocaleString("tr-TR", { day: "2-digit", month: "2-digit", year: "numeric", hour: "2-digit", minute: "2-digit" })}
                      </p>
                    </div>
                    <div className="text-right">
                      <p className="tabular text-sm font-bold text-brand-green">{fmtTL(r.total)}</p>
                      <p className="text-[10px] text-slate-400">{r.lines.length} kalem</p>
                    </div>
                    <ChevronDown className={`h-4 w-4 text-slate-400 transition-transform ${open ? "rotate-180" : ""}`} />
                  </button>
                  {open && (
                    <div className="bg-slate-50/60 px-4 pb-3">
                      {r.note && <p className="mb-2 text-[11px] text-slate-500">Not: {r.note}</p>}
                      {r.document && (
                        <div data-testid={`hist-doc-${r.id}`} className="mb-2 rounded-md border border-slate-200 bg-white p-2 text-[11px] text-slate-600">
                          <p className="mb-1 flex flex-wrap items-center gap-x-3 gap-y-0.5">
                            <span className="inline-flex items-center gap-1 font-semibold text-slate-700"><Receipt className="h-3 w-3 text-brand-green" /> {r.document.doc_type_label}{r.document.doc_no ? `: ${r.document.doc_no}` : ""}{r.document.doc_detail ? ` — ${r.document.doc_detail}` : ""}</span>
                            {r.document.delivered_by && <span>Getiren: <b>{r.document.delivered_by}</b>{r.document.delivered_by_phone ? ` · ${r.document.delivered_by_phone}` : ""}</span>}
                          </p>
                          {(r.document.doc_urls || []).length > 0 && (
                            <div className="flex flex-wrap gap-1.5">
                              {r.document.doc_urls.map((u, i) => (
                                <a key={i} href={fileHref(u)} target="_blank" rel="noreferrer" className="inline-flex items-center gap-1 rounded-full bg-emerald-50 px-2 py-0.5 text-emerald-700 ring-1 ring-inset ring-emerald-200 hover:bg-emerald-100">
                                  <Truck className="h-3 w-3" /> Belge {i + 1}
                                </a>
                              ))}
                            </div>
                          )}
                        </div>
                      )}
                      <div className="overflow-hidden rounded-md ring-1 ring-inset ring-slate-200">
                        <table className="w-full text-xs">
                          <thead className="bg-slate-100 text-left text-[10px] text-slate-500">
                            <tr>
                              <th className="px-3 py-2 font-medium">Ürün</th>
                              <th className="px-3 py-2 text-right font-medium">Miktar</th>
                              <th className="px-3 py-2 text-right font-medium">Birim Alış</th>
                              <th className="px-3 py-2 text-right font-medium">Tutar</th>
                              <th className="px-3 py-2 font-medium">SKT</th>
                            </tr>
                          </thead>
                          <tbody className="divide-y divide-slate-100 bg-white">
                            {r.lines.map((l, i) => (
                              <tr key={i} data-testid={`hist-line-${r.id}-${i}`}>
                                <td className="px-3 py-2"><span className="font-medium text-slate-800">{l.name}</span> <span className="font-mono text-[10px] text-slate-400">{l.sku}</span></td>
                                <td className="tabular px-3 py-2 text-right text-slate-700">{l.quantity}</td>
                                <td className="tabular px-3 py-2 text-right text-slate-700">{fmtTL(l.unit_cost)}</td>
                                <td className="tabular px-3 py-2 text-right font-semibold text-slate-900">{fmtTL(l.line_total)}</td>
                                <td className="px-3 py-2">{l.expiry_date ? <ExpiryBadge dateStr={l.expiry_date} /> : <span className="text-slate-300">—</span>}</td>
                              </tr>
                            ))}
                          </tbody>
                        </table>
                      </div>
                    </div>
                  )}
                </div>
              );
            })}
          </div>
        )}
      </div>
    </div>
  );
}
